import { subscribeKey } from 'valtio/utils';

import { Deferrable } from '@canvas/deferrable';
import { NoopContentAPI } from '@canvas/frame-api-noop';
import {
  type WhiteboardSessionData,
  type RawContentAPI,
  type WhiteboardQueryParams,
  type OnboardingStartHandler,
  type RawFrameToken,
  type RawTokenHandler,
  type RouteHandler,
  type ContainerHandler,
} from '@canvas/frame-api-types';
import { unlockAllPaywallsResponseMock } from '@canvas/paywall-test-utils';
import { currentSidebarPanel } from '@canvas/sidebar-panel-state';
import { getQueryParams } from '@canvas/utils';

import { getTokenFromLocalServer, getTokenFromSite } from './get-access-token';
import { getBoardId } from './get-board-id';
import { getSpaceId } from './get-space-id';

const developmentAccountId = process.env.VITE_CANVAS_EAP_ACCOUNT_ID ?? '';
const developmentCloudId = process.env.VITE_CANVAS_EAP_CLOUD_ID ?? '';

const developmentSessionData: WhiteboardSessionData = {
  accountId: developmentAccountId,
  tenantId: developmentCloudId,
};

const extractQueryParams = (query?: WhiteboardQueryParams): URLSearchParams => {
  const queryParams = query ?? {};
  const queryStringified = Object.fromEntries(
    Object.entries(queryParams).map(([k, v]) => [k, `${v}`])
  );

  // TODO - fix passing of query params after routing changes land
  const params = new URLSearchParams(queryStringified);
  void params;

  return params;
};

const requestFrameToken = (boardId: string) => {
  const localAsapToken = process.env.VITE_LOCAL_ASAP_TOKEN;
  const localSpaceId = process.env.VITE_LOCAL_SPACE_ID;

  const isLocalDevLoop = localAsapToken && localSpaceId;

  if (globalThis.ATLASSIAN_CANVAS?.frameToken !== undefined) {
    return Promise.resolve(globalThis.ATLASSIAN_CANVAS.frameToken as RawFrameToken);
  }

  return isLocalDevLoop
    ? getTokenFromLocalServer({
        boardId,
        localSpaceId,
        localAsapToken,
        developmentCloudId,
      })
    : getTokenFromSite({
        boardId,
        developmentSpaceId: getSpaceId(),
        developmentCloudId,
      });
};

export class ContentAPILocal extends NoopContentAPI implements RawContentAPI {
  public sessionData = developmentSessionData;

  private tokenChangeHandler: RawTokenHandler | undefined;
  private routeChangeHandler: RouteHandler | undefined;
  private fetching: Promise<RawFrameToken> | undefined;

  public goToContent = async (id: string, query?: WhiteboardQueryParams) => {
    const token = await this.getToken();

    const windowParams = getQueryParams();
    const providedParams = extractQueryParams(query);
    const params = new URLSearchParams({
      ...Object.fromEntries(windowParams),
      ...Object.fromEntries(providedParams),
    });

    this.tokenChangeHandler?.(token);
    this.routeChangeHandler?.(
      `/whiteboard/${id}${params.size > 0 ? `?${params.toString()}` : ''}`
    );
  };

  public onTokenInit = async (handler: RawTokenHandler) => {
    const token = await this.getToken();
    handler(token);
  };

  public onTokenChange = (handler: RawTokenHandler) => {
    this.tokenChangeHandler = handler;
  };

  public onRouteChange = (handler: (route: string) => void) => {
    this.routeChangeHandler = handler;
  };

  public onContainerInit = (handler: ContainerHandler) => {
    handler({
      key: process.env.VITE_CANVAS_EAP_CONTAINER ?? '',
      name: process.env.VITE_CANVAS_EAP_CONTAINER_NAME ?? '',
      source: 'Confluence',
    });
  };

  public getToken = async (): Promise<RawFrameToken> => {
    const boardId = getBoardId();
    if (!boardId) {
      return {
        container: '',
        board: '',
        accessToken: '',
        mediaToken: '',
        mediaBaseUrl: '',
        mediaCollectionId: '',
        features: unlockAllPaywallsResponseMock,
      };
    }

    if (this.fetching) {
      return this.fetching;
    }

    const tokenPromise = requestFrameToken(boardId);

    this.fetching = tokenPromise;

    return await tokenPromise.then((data) => {
      this.fetching = undefined;
      return data;
    });
  };

  public showEditionUpgradeCTA = () => {
    const deferrable = new Deferrable<void>();

    currentSidebarPanel.panel = 'local-paywall-cta-panel';

    // Subscribe to next panel update, when it changes we know that this panel
    // was closed and can resolve our deferred promise
    const unsubscribe = subscribeKey(currentSidebarPanel, 'panel', () => {
      deferrable.resolve();

      unsubscribe();
    });

    return deferrable.get();
  };

  public closeEditionUpgradeCTA = () => {
    currentSidebarPanel.panel = 'none';
  };

  public registerStartOnboardingCallback = (cb: OnboardingStartHandler) => {
    (window as any).startOnboarding = cb;
  };

  public platformFeatureFlagResolver = (_name: string) => true;
}

export const contentAPILocal = new ContentAPILocal();
